<script lang="ts">
  import * as leaflet from 'leaflet';
  import type { LatLngTuple, Layer, LeafletMouseEvent, Map as LeafletMap } from 'leaflet';
  import { SvelteURLSearchParams } from 'svelte/reactivity';
  import { ApiError, apiGet, getRoutes } from '../lib/api';
  import {
    clampRadius,
    nearbyQuery,
    RADIUS_MAX_M,
    RADIUS_MIN_M,
    vertexStyle,
  } from '../lib/debug-graph';
  import { parseCoord, type Coord } from '../lib/debug-walk';
  import type { Endpoint, EndpointPickSource } from '../lib/endpoint';
  import { formatLatLon } from '../lib/endpoint';
  import type { NearbyResponse, NearbyVertex } from '../lib/nearby-types';
  import { decodePenUrl, encodePenUrl, type PenEndpointSlot } from '../lib/pen-url';
  import { buildRouteQuery } from '../lib/route-query';
  import {
    DEFAULT_CENTRE,
    DEFAULT_CENTRE_TEXT,
    DEFAULT_GRAPH_RADIUS_M,
    DEFAULT_SNAP_RADIUS_M,
    DEFAULT_ZOOM,
    DESTINATION_PIN_COLOR,
    formatJourneyLine,
    GRAPH_MARKER_RADIUS,
    journeyWalkGeometry,
    type JourneyStatus,
    ORIGIN_PIN_COLOR,
    parseSnapRadius,
    parseZoom,
    PIN_RADIUS,
    snapToNearest,
    TRACE_COLOR,
    TRACE_DASH,
    TRACE_WEIGHT,
    traceToJson,
    VERTEX_FILL_COLOR,
    VERTEX_MARKER_RADIUS,
    WALK_VERTEX_COLOR,
    WALK_VERTEX_RADIUS,
  } from '../lib/pen';
  import type { JourneyJson } from '../types/JourneyJson';
  import ClipboardButton from './clipboard-button.svelte';
  import EndpointInput from './endpoint-input.svelte';
  import MapView from './map.svelte';
  import SwapButton from './swap-button.svelte';

  /** Local rune-tracked store for the pen page. */
  interface PenState {
    /** User-drawn polyline, in the order clicked. */
    trace: Coord[];
    /** When false, map clicks are ignored (so panning doesn't drop stray vertices). */
    drawMode: boolean;
    /** Centre coord as typed — parsed on every Recentre click. */
    centreText: string;
    /** Zoom level as typed — parsed on every Recentre click. */
    zoomText: string;
    /** When true, `handleMapClick` snaps to the nearest cached graph vertex. */
    snapEnabled: boolean;
    /** Snap radius in metres as typed — parsed on each click while snap is enabled. */
    snapRadiusText: string;
    /** When true, the graph overlay is drawn onto the map (subject to `graphVertices`). */
    graphOverlayEnabled: boolean;
    /** Overlay-fetch radius (metres) as typed — clamped on Query graph. */
    graphRadiusText: string;
    /** Cached /v1/debug/nearby vertices used for snap + overlay rendering. */
    graphVertices: NearbyVertex[];
    /** Journey returned by the last /v1/routes call, or `undefined` when none. */
    journey: JourneyJson | undefined;
    /** Tracks whether the last search yielded a journey, no journey, or hasn't run. */
    journeyStatus: JourneyStatus;
    /** Origin endpoint picked from the From input or a map click. */
    originEndpoint: Endpoint | undefined;
    /** Text currently shown in the From input — mirrors picks + map clicks. */
    originText: string;
    /** Source coord of the last origin pick — used to render a pin for stop endpoints. */
    originSource: EndpointPickSource | undefined;
    /** Destination endpoint picked from the To input. */
    destinationEndpoint: Endpoint | undefined;
    /** Text currently shown in the To input. */
    destinationText: string;
    /** Source coord of the last destination pick — used to render a pin for stop endpoints. */
    destinationSource: EndpointPickSource | undefined;
    /** Names the fetch currently in flight so buttons can label + disable individually. */
    loadingOp: 'graph' | 'journey' | undefined;
    /** Human-readable error message shown in the sidebar banner; empty hides it. */
    errorMessage: string;
  }

  /** Extracts the pen search-string ("?origin_lat=…&dest_…") from the current URL hash. */
  function initialUrlSearch(): string {
    const questionAt = location.hash.indexOf('?');
    return questionAt === -1 ? '' : location.hash.slice(questionAt);
  }

  const restoredState = decodePenUrl(initialUrlSearch());

  const state = $state<PenState>({
    trace: [],
    drawMode: false,
    centreText: DEFAULT_CENTRE_TEXT,
    zoomText: String(DEFAULT_ZOOM),
    snapEnabled: false,
    snapRadiusText: String(DEFAULT_SNAP_RADIUS_M),
    graphOverlayEnabled: false,
    graphRadiusText: String(DEFAULT_GRAPH_RADIUS_M),
    graphVertices: [],
    journey: undefined,
    journeyStatus: 'none',
    originEndpoint: restoredState.origin.endpoint,
    originText: restoredState.origin.text,
    originSource: restoredState.origin.source,
    destinationEndpoint: restoredState.destination.endpoint,
    destinationText: restoredState.destination.text,
    destinationSource: restoredState.destination.source,
    loadingOp: undefined,
    errorMessage: '',
  });

  /** Live JSON view of the current trace — also fed to the Copy button. */
  const traceJson = $derived(traceToJson(state.trace));
  /** Pre-formatted trace-count line for the sidebar. */
  const vertexCountLine = $derived(`Trace: ${String(state.trace.length)} vertices`);
  /** Pre-formatted graph-count line for the sidebar. */
  const graphCountLine = $derived(`Graph: ${String(state.graphVertices.length)} vertices`);
  /** Journey status line matching whichever branch — no journey / loaded / empty search. */
  const journeyLine = $derived(formatJourneyLine(state.journeyStatus));
  /** Concatenated walking-leg geometry of the shown journey, in the trace's tuple shape. */
  const walkGeometry = $derived(journeyWalkGeometry(state.journey));
  /** Copy-button payload — same pretty JSON format as `traceJson` so both paste identically. */
  const walkJson = $derived(traceToJson(walkGeometry));
  /** Search button is unusable until both endpoints are set or while any fetch is in flight. */
  const canSearchJourney = $derived(
    state.originEndpoint !== undefined &&
      state.destinationEndpoint !== undefined &&
      state.loadingOp === undefined,
  );
  /** Label swap so the button reads "Loading…" only while its own fetch is running. */
  const journeyButtonLabel = $derived(
    state.loadingOp === 'journey' ? 'Loading…' : 'Search journey',
  );
  /** Same idea for the graph-overlay fetch button. */
  const graphButtonLabel = $derived(state.loadingOp === 'graph' ? 'Loading…' : 'Query graph');
  /** Origin pin coord — set when origin endpoint has a known lat/lon (position or stop). */
  const originPin = $derived(pinCoord(state.originEndpoint, state.originSource));
  /** Destination pin coord — same logic as `originPin`. */
  const destinationPin = $derived(pinCoord(state.destinationEndpoint, state.destinationSource));
  /** Serialised URL search string for the current endpoints — fed to the Copy URL button. */
  const penUrlSearch = $derived(
    encodePenUrl({
      origin: currentSlot(state.originEndpoint, state.originText, state.originSource),
      destination: currentSlot(
        state.destinationEndpoint,
        state.destinationText,
        state.destinationSource,
      ),
    }),
  );

  const mapHolder: { map: LeafletMap | undefined } = { map: undefined };
  const traceLayers: { polyline: Layer | undefined; markers: Layer[] } = {
    polyline: undefined,
    markers: [],
  };
  const graphLayers: { markers: Layer[] } = { markers: [] };
  const walkLayers: { markers: Layer[] } = { markers: [] };
  const pinLayers: {
    origin: Layer | undefined;
    destination: Layer | undefined;
    originCoord: Coord | undefined;
    destinationCoord: Coord | undefined;
  } = {
    origin: undefined,
    destination: undefined,
    originCoord: undefined,
    destinationCoord: undefined,
  };

  /** Converts a `Coord` (readonly tuple) into Leaflet's mutable `LatLngTuple`. */
  function toTuple(coord: Coord): LatLngTuple {
    return [coord[0], coord[1]];
  }

  /**
   * Runs `action` against the current Leaflet map, or no-ops when the map is not
   * yet mounted. Every `$effect` that mutates the map goes through this so the
   * "map not ready" guard lives in exactly one place.
   */
  function withMap(action: (mapInstance: LeafletMap) => void): void {
    const mapInstance = mapHolder.map;
    /* istanbul ignore if -- effects re-run when their reactive deps change; the map is mounted by then. */
    if (!mapInstance) return;
    action(mapInstance);
  }

  /** Stashes the Leaflet map instance and wires up click-to-trace. */
  function handleMapReady(mapInstance: LeafletMap): void {
    mapHolder.map = mapInstance;
    mapInstance.on('click', handleMapClick);
  }

  /** Clears the cached map + click handler when MapView unmounts. */
  function handleMapDestroy(): void {
    const previous = mapHolder.map;
    /* istanbul ignore else -- onDestroy fires after handleMapReady set mapHolder.map. */
    if (previous) previous.off('click', handleMapClick);
    mapHolder.map = undefined;
    pinLayers.origin = undefined;
    pinLayers.destination = undefined;
    pinLayers.originCoord = undefined;
    pinLayers.destinationCoord = undefined;
  }

  /** Leaflet click handler — Draw mode appends trace vertices, otherwise sets the origin. */
  function handleMapClick(event: LeafletMouseEvent): void {
    const clickCoord: Coord = [event.latlng.lat, event.latlng.lng];
    if (state.drawMode) {
      state.trace = [...state.trace, maybeSnap(clickCoord)];
      return;
    }
    setOriginFromCoord(clickCoord);
  }

  /** Formats `coord` as the input-facing `"lat, lon"` string. */
  function formatCoordText(coord: Coord): string {
    return formatLatLon(coord[0], coord[1]);
  }

  /** Populates the From input + originEndpoint from a coord picked off the map. */
  function setOriginFromCoord(coord: Coord): void {
    state.originEndpoint = { kind: 'position', latitude: coord[0], longitude: coord[1] };
    state.originText = formatCoordText(coord);
    state.originSource = undefined;
  }

  /** Turns a raw text value into a position endpoint when the text parses as `"lat,lon"`. */
  function endpointFromText(text: string): Endpoint | undefined {
    const coord = parseCoord(text);
    if (!coord) return undefined;
    return { kind: 'position', latitude: coord[0], longitude: coord[1] };
  }

  /**
   * From input's `oninput` — updates the text and reparses the endpoint. A valid
   * `"lat,lon"` becomes a position endpoint; every other keystroke clears the
   * committed endpoint until the user picks another suggestion or a map click.
   */
  function handleOriginInput(next: string): void {
    state.originText = next;
    state.originEndpoint = endpointFromText(next);
    state.originSource = undefined;
  }

  /** From input's `onPick` — commits a picked endpoint + its label text and its source coord. */
  function handleOriginPick(endpoint: Endpoint, label: string, source?: EndpointPickSource): void {
    state.originEndpoint = endpoint;
    state.originText = label;
    state.originSource = source;
  }

  /** To input's `oninput` — same reparse-or-clear behaviour as {@link handleOriginInput}. */
  function handleDestinationInput(next: string): void {
    state.destinationText = next;
    state.destinationEndpoint = endpointFromText(next);
    state.destinationSource = undefined;
  }

  /** To input's `onPick`. */
  function handleDestinationPick(
    endpoint: Endpoint,
    label: string,
    source?: EndpointPickSource,
  ): void {
    state.destinationEndpoint = endpoint;
    state.destinationText = label;
    state.destinationSource = source;
  }

  /**
   * Coord to render a pin at. Position endpoints use the endpoint's own lat/lon;
   * stop and address endpoints use the picked-suggestion `source` when available.
   */
  function pinCoord(
    endpoint: Endpoint | undefined,
    source: EndpointPickSource | undefined,
  ): Coord | undefined {
    if (!endpoint) return undefined;
    if (endpoint.kind === 'position') return [endpoint.latitude, endpoint.longitude];
    if (source) return [source.latitude, source.longitude];
    return undefined;
  }

  /** Packs one endpoint slot into the shape `encodePenUrl` expects. */
  function currentSlot(
    endpoint: Endpoint | undefined,
    text: string,
    source: EndpointPickSource | undefined,
  ): PenEndpointSlot {
    return { endpoint, text, source };
  }

  /** Absolute URL to share — the base without any search, plus the encoded pen state. */
  function shareableUrl(search: string): string {
    const base = `${location.origin}${location.pathname}#/pen`;
    return search ? `${base}${search}` : base;
  }

  /** Swaps the two endpoints, their input texts, and their source coords atomically. */
  function swapEndpoints(): void {
    const priorOriginEndpoint = state.originEndpoint;
    const priorOriginText = state.originText;
    const priorOriginSource = state.originSource;
    state.originEndpoint = state.destinationEndpoint;
    state.originText = state.destinationText;
    state.originSource = state.destinationSource;
    state.destinationEndpoint = priorOriginEndpoint;
    state.destinationText = priorOriginText;
    state.destinationSource = priorOriginSource;
  }

  /** Applies snap-to-nearest-graph-vertex when enabled; passes coord through otherwise. */
  function maybeSnap(coord: Coord): Coord {
    if (!state.snapEnabled) return coord;
    const radius = parseSnapRadius(state.snapRadiusText);
    return snapToNearest(coord, state.graphVertices, radius);
  }

  /** Removes the last vertex from the trace. No-op on an empty trace. */
  function undoVertex(): void {
    state.trace = state.trace.slice(0, -1);
  }

  /** Empties the trace only; the searched journey stays visible until Clear journey. */
  function clearTrace(): void {
    state.trace = [];
  }

  /** Clears the currently-rendered journey without touching the trace. */
  function clearJourney(): void {
    state.journey = undefined;
    state.journeyStatus = 'none';
  }

  /** Parses the centre + zoom inputs and re-centres the map. */
  function recentre(): void {
    const mapInstance = mapHolder.map;
    /* istanbul ignore if -- Recentre is only clickable once MapView has invoked onReady. */
    if (!mapInstance) return;
    state.errorMessage = '';
    const centre = parseCoord(state.centreText);
    if (!centre) {
      reportInvalidCentre();
      return;
    }
    const zoom = parseZoom(state.zoomText) ?? mapInstance.getZoom();
    mapInstance.setView(toTuple(centre), zoom);
  }

  /** Loads /v1/debug/nearby into `state.graphVertices`. */
  async function loadGraph(centre: Coord, radiusMetres: number): Promise<void> {
    const response = await apiGet<NearbyResponse>(
      '/v1/debug/nearby',
      nearbyQuery(centre, radiusMetres),
    );
    state.graphVertices = [...response.vertices];
  }

  /** Loads /v1/routes for the given endpoints and stores the first returned journey. */
  async function loadJourney(origin: Endpoint, destination: Endpoint): Promise<void> {
    const query = buildRouteQuery(origin, destination);
    const response = await getRoutes(query);
    const firstJourney = response.journeys.at(0);
    state.journey = firstJourney;
    state.journeyStatus = firstJourney ? 'loaded' : 'empty';
  }

  /** Renders `error` in the sidebar; uses `ApiError.message` verbatim when available. */
  function reportError(error: unknown): void {
    state.errorMessage = error instanceof ApiError ? error.message : 'Request failed.';
  }

  /** Records the "bad centre coord" message. */
  function reportInvalidCentre(): void {
    state.errorMessage = 'Enter the centre as "lat,lon".';
  }

  /** Guards `action` behind the shared loading marker + error handler. */
  async function withLoading(
    operation: 'graph' | 'journey',
    action: () => Promise<void>,
  ): Promise<void> {
    state.loadingOp = operation;
    state.errorMessage = '';
    try {
      await action();
    } catch (error: unknown) {
      reportError(error);
    } finally {
      state.loadingOp = undefined;
    }
  }

  /** Parses centre + clamps graph radius, then dispatches through `withLoading`. */
  async function fetchGraph(): Promise<void> {
    const centre = parseCoord(state.centreText);
    if (!centre) {
      reportInvalidCentre();
      return;
    }
    const radiusMetres = clampRadius(Number(state.graphRadiusText));
    await withLoading('graph', () => loadGraph(centre, radiusMetres));
  }

  /** Fetches a journey between the two currently-picked endpoints. */
  async function fetchJourney(): Promise<void> {
    const origin = state.originEndpoint;
    const destination = state.destinationEndpoint;
    /* istanbul ignore if -- Search button is disabled until both endpoints are set. */
    if (!origin || !destination) return;
    await withLoading('journey', () => loadJourney(origin, destination));
  }

  /** Removes cached trace layers from `mapInstance` and drops the cache. */
  function removeTraceLayers(mapInstance: LeafletMap): void {
    if (traceLayers.polyline) {
      mapInstance.removeLayer(traceLayers.polyline);
      traceLayers.polyline = undefined;
    }
    for (const marker of traceLayers.markers) mapInstance.removeLayer(marker);
    traceLayers.markers = [];
  }

  /** Adds one small circle at `coord` and caches it for later removal. */
  function addTraceMarker(mapInstance: LeafletMap, coord: Coord): void {
    const marker = leaflet
      .circleMarker(toTuple(coord), {
        radius: VERTEX_MARKER_RADIUS,
        color: TRACE_COLOR,
        weight: 2,
        fillColor: VERTEX_FILL_COLOR,
        fillOpacity: 1,
      })
      .addTo(mapInstance);
    traceLayers.markers.push(marker);
  }

  /** Renders the trace polyline + per-vertex circles fresh onto `mapInstance`. */
  function redrawTrace(mapInstance: LeafletMap, trace: readonly Coord[]): void {
    removeTraceLayers(mapInstance);
    if (trace.length > 1) {
      traceLayers.polyline = leaflet
        .polyline(
          trace.map((coord) => toTuple(coord)),
          {
            color: TRACE_COLOR,
            weight: TRACE_WEIGHT,
            dashArray: TRACE_DASH,
            opacity: 0.9,
          },
        )
        .addTo(mapInstance);
    }
    for (const coord of trace) addTraceMarker(mapInstance, coord);
  }

  /** Removes cached graph-overlay markers from `mapInstance` and drops the cache. */
  function removeGraphLayers(mapInstance: LeafletMap): void {
    for (const marker of graphLayers.markers) mapInstance.removeLayer(marker);
    graphLayers.markers = [];
  }

  /** Adds one coloured circle for a single graph vertex, styled per `vertexStyle`. */
  function addGraphMarker(mapInstance: LeafletMap, vertex: NearbyVertex): void {
    const style = vertexStyle(vertex);
    const marker = leaflet
      .circleMarker(toTuple(vertex.coord), {
        radius: GRAPH_MARKER_RADIUS,
        color: style.color,
        weight: 1,
        fillColor: style.color,
        fillOpacity: 0.7,
      })
      .addTo(mapInstance);
    graphLayers.markers.push(marker);
  }

  /** Wipes any drawn graph markers and re-adds them when overlay is enabled. */
  function redrawGraph(
    mapInstance: LeafletMap,
    vertices: readonly NearbyVertex[],
    isEnabled: boolean,
  ): void {
    removeGraphLayers(mapInstance);
    if (!isEnabled) return;
    for (const vertex of vertices) addGraphMarker(mapInstance, vertex);
  }

  /** Removes cached walk-vertex markers from `mapInstance` and drops the cache. */
  function removeWalkLayers(mapInstance: LeafletMap): void {
    for (const marker of walkLayers.markers) mapInstance.removeLayer(marker);
    walkLayers.markers = [];
  }

  /** Adds one small blue circle at `coord` and caches it for later removal. */
  function addWalkVertexMarker(mapInstance: LeafletMap, coord: Coord): void {
    const marker = leaflet
      .circleMarker(toTuple(coord), {
        radius: WALK_VERTEX_RADIUS,
        color: WALK_VERTEX_COLOR,
        weight: 1,
        fillColor: WALK_VERTEX_COLOR,
        fillOpacity: 0.9,
      })
      .addTo(mapInstance);
    walkLayers.markers.push(marker);
  }

  /** Renders one marker per vertex in the loaded journey's walking geometry. */
  function redrawWalk(mapInstance: LeafletMap, coords: readonly Coord[]): void {
    removeWalkLayers(mapInstance);
    for (const coord of coords) addWalkVertexMarker(mapInstance, coord);
  }

  $effect(() => {
    const trace = state.trace;
    withMap((mapInstance) => {
      redrawTrace(mapInstance, trace);
    });
  });

  $effect(() => {
    const isEnabled = state.graphOverlayEnabled;
    const vertices = state.graphVertices;
    withMap((mapInstance) => {
      redrawGraph(mapInstance, vertices, isEnabled);
    });
  });

  $effect(() => {
    const isDrawing = state.drawMode;
    withMap((mapInstance) => {
      mapInstance.getContainer().style.cursor = isDrawing ? 'crosshair' : '';
    });
  });

  $effect(() => {
    const coords = walkGeometry;
    withMap((mapInstance) => {
      redrawWalk(mapInstance, coords);
    });
  });

  /** Removes any prior pin layer at `slot` from `mapInstance` and clears its cached coord. */
  function removePin(mapInstance: LeafletMap, slot: 'origin' | 'destination'): void {
    const marker = pinLayers[slot];
    if (marker) mapInstance.removeLayer(marker);
    pinLayers[slot] = undefined;
    if (slot === 'origin') pinLayers.originCoord = undefined;
    else pinLayers.destinationCoord = undefined;
  }

  /** True when `coord` matches `prior` on both lat and lon exactly. */
  function isSameCoord(coord: Coord, prior: Coord | undefined): boolean {
    return prior !== undefined && prior[0] === coord[0] && prior[1] === coord[1];
  }

  /** Reads the cached coord (if any) currently displayed for `slot`. */
  function priorPinCoord(slot: 'origin' | 'destination'): Coord | undefined {
    return slot === 'origin' ? pinLayers.originCoord : pinLayers.destinationCoord;
  }

  /** Draws a fresh pin at `coord` for `slot` and records it as the cached coord. */
  function drawPin(
    mapInstance: LeafletMap,
    slot: 'origin' | 'destination',
    coord: Coord,
    color: string,
  ): void {
    removePin(mapInstance, slot);
    pinLayers[slot] = leaflet
      .circleMarker(toTuple(coord), {
        radius: PIN_RADIUS,
        color,
        weight: 2,
        fillColor: color,
        fillOpacity: 0.9,
      })
      .addTo(mapInstance);
    if (slot === 'origin') pinLayers.originCoord = coord;
    else pinLayers.destinationCoord = coord;
  }

  /** Renders (or removes) the origin / destination pin marker; no-op when unchanged. */
  function paintPin(
    mapInstance: LeafletMap,
    slot: 'origin' | 'destination',
    coord: Coord | undefined,
    color: string,
  ): void {
    if (!coord) {
      removePin(mapInstance, slot);
      return;
    }
    if (isSameCoord(coord, priorPinCoord(slot))) return;
    drawPin(mapInstance, slot, coord, color);
  }

  $effect(() => {
    const coord = originPin;
    withMap((mapInstance) => {
      paintPin(mapInstance, 'origin', coord, ORIGIN_PIN_COLOR);
    });
  });

  $effect(() => {
    const coord = destinationPin;
    withMap((mapInstance) => {
      paintPin(mapInstance, 'destination', coord, DESTINATION_PIN_COLOR);
    });
  });

  /** Names of the URL query keys the pen page owns; every other key is preserved on sync. */
  const OWNED_URL_KEYS: ReadonlySet<string> = new Set([
    'origin_lat',
    'origin_lon',
    'origin_stop',
    'origin_addr',
    'origin_label',
    'dest_lat',
    'dest_lon',
    'dest_stop',
    'dest_addr',
    'dest_label',
  ]);

  /** Millisecond delay before URL sync writes the hash, so a burst of keystrokes coalesces. */
  const URL_SYNC_DEBOUNCE_MS = 250;

  const urlSyncState: { timer: ReturnType<typeof setTimeout> | undefined } = { timer: undefined };

  /** Parses the current `#/pen?...` hash into a search-params bag; empty when no query is present. */
  function foreignHashParameters(): SvelteURLSearchParams {
    const marker = '#/pen?';
    if (!location.hash.startsWith(marker)) return new SvelteURLSearchParams();
    const parameters = new SvelteURLSearchParams(location.hash.slice(marker.length));
    for (const ownedKey of OWNED_URL_KEYS) parameters.delete(ownedKey);
    return parameters;
  }

  /** Merges the pen-owned encoded search over any foreign hash params currently present. */
  function mergedHash(ownedSearch: string): string {
    const merged = foreignHashParameters();
    const trimmed = ownedSearch.startsWith('?') ? ownedSearch.slice(1) : ownedSearch;
    const owned = new SvelteURLSearchParams(trimmed);
    for (const [ownedKey, ownedValue] of owned) merged.set(ownedKey, ownedValue);
    const combined = merged.toString();
    return combined ? `#/pen?${combined}` : '#/pen';
  }

  /** Schedules a debounced hash rewrite; the latest call wins. */
  function scheduleUrlSync(nextHash: string): void {
    if (urlSyncState.timer !== undefined) clearTimeout(urlSyncState.timer);
    urlSyncState.timer = setTimeout(() => {
      urlSyncState.timer = undefined;
      if (location.hash !== nextHash) history.replaceState(null, '', nextHash);
    }, URL_SYNC_DEBOUNCE_MS);
  }

  $effect(() => {
    const search = penUrlSearch;
    scheduleUrlSync(mergedHash(search));
  });
</script>

<!-- Developer pen tool: draw a polyline on the map, copy the vertex list as JSON,
     optionally overlay the walking-graph vertices, and search a journey between
     the trace's first and last vertex to see the algorithm's actual walk.
     Not password-gated — reach it at /#/pen. -->
<section class="pen">
  <div class="map-wrap">
    <MapView
      onReady={handleMapReady}
      onDestroy={handleMapDestroy}
      journey={state.journey}
      center={[DEFAULT_CENTRE[0], DEFAULT_CENTRE[1]]}
      zoom={DEFAULT_ZOOM}
    />
  </div>
  <aside class="side" aria-label="Pen controls">
    <header class="head">
      <h1>Pen</h1>
      <p class="hint">
        Click the map to set From, type or paste <code>lat,lon</code> or an address into either field,
        then Search. Toggle Draw mode to switch clicks into trace-vertex mode instead.
      </p>
    </header>

    {#if state.errorMessage}
      <p class="error" role="alert" aria-live="assertive">{state.errorMessage}</p>
    {/if}

    <div class="box mode">
      <label class="checkbox">
        <input type="checkbox" bind:checked={state.drawMode} aria-label="Draw mode" />
        <strong>Draw mode</strong> — clicks add vertices (cursor becomes crosshair)
      </label>
    </div>

    <div class="box">
      <label class="field">
        <span>Centre (lat,lon)</span>
        <input
          type="text"
          class="io-input"
          bind:value={state.centreText}
          aria-label="Centre coord"
        />
      </label>
      <label class="field">
        <span>Zoom</span>
        <input type="text" class="io-input" bind:value={state.zoomText} aria-label="Zoom level" />
      </label>
      <button type="button" onclick={recentre}>Recentre</button>
    </div>

    <div class="box">
      <p class="count">{vertexCountLine}</p>
      <div class="row">
        <button type="button" onclick={undoVertex} disabled={state.trace.length === 0}>
          Undo
        </button>
        <button type="button" onclick={clearTrace} disabled={state.trace.length === 0}>
          Clear
        </button>
        <ClipboardButton getText={() => traceJson} label="Copy JSON" />
      </div>
      <label class="field">
        <span>Live JSON</span>
        <textarea class="io-input" readonly aria-label="Trace JSON" rows="8">{traceJson}</textarea>
      </label>
    </div>

    <div class="box">
      <EndpointInput
        label="From"
        scope="pen"
        value={state.originText}
        onInput={handleOriginInput}
        onPick={handleOriginPick}
        allowPosition
      />
      <SwapButton onSwap={swapEndpoints} />
      <EndpointInput
        label="To"
        scope="pen"
        value={state.destinationText}
        onInput={handleDestinationInput}
        onPick={handleDestinationPick}
        allowPosition
      />
      <div class="row">
        <button
          type="button"
          onclick={() => {
            void fetchJourney();
          }}
          disabled={!canSearchJourney}
        >
          {journeyButtonLabel}
        </button>
        <ClipboardButton getText={() => walkJson} label="Copy walk JSON" />
        <ClipboardButton getText={() => shareableUrl(penUrlSearch)} label="Copy URL" />
        <button type="button" onclick={clearJourney} disabled={state.journey === undefined}>
          Clear journey
        </button>
      </div>
      <p class="count">{journeyLine}</p>
    </div>

    <div class="box">
      <label class="checkbox">
        <input type="checkbox" bind:checked={state.snapEnabled} aria-label="Snap enabled" />
        Snap to nearest graph vertex within…
      </label>
      <label class="field">
        <span>Snap radius (m)</span>
        <input
          type="text"
          class="io-input"
          bind:value={state.snapRadiusText}
          aria-label="Snap radius metres"
        />
      </label>
    </div>

    <div class="box">
      <label class="checkbox">
        <input
          type="checkbox"
          bind:checked={state.graphOverlayEnabled}
          aria-label="Overlay enabled"
        />
        Show walking-graph points on map
      </label>
      <p class="hint">The vertices the router uses for walking and snap-to.</p>
      <label class="field">
        <span>Overlay radius (m)</span>
        <input
          type="number"
          class="io-input"
          min={RADIUS_MIN_M}
          max={RADIUS_MAX_M}
          step="50"
          bind:value={state.graphRadiusText}
          aria-label="Overlay radius metres"
        />
      </label>
      <button
        type="button"
        onclick={() => {
          void fetchGraph();
        }}
        disabled={state.loadingOp !== undefined}
      >
        {graphButtonLabel}
      </button>
      <p class="count">{graphCountLine}</p>
    </div>
  </aside>
</section>

<style>
  .pen {
    display: grid;
    grid-template-columns: 1fr 340px;
    height: 100vh;
    box-sizing: border-box;
    background: var(--color-bg);
    color: var(--color-fg);
    font-family: var(--font-sans);
  }

  .map-wrap {
    min-height: 0;
  }

  .side {
    overflow-y: auto;
    padding: var(--space-md);
    border-left: 1px solid var(--color-border);
    background: var(--color-surface);
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
    font-size: var(--font-size-sm);
  }

  .head h1 {
    margin: 0;
    font-size: var(--font-size-xl);
    color: var(--color-fg);
  }

  .hint {
    margin: var(--space-xs) 0 0;
    font-size: var(--font-size-xs);
    color: var(--color-fg-muted);
  }

  .box {
    border: 1px solid var(--color-border);
    background: var(--color-surface-alt);
    padding: var(--space-sm);
    border-radius: var(--radius-sm);
    display: flex;
    flex-direction: column;
    gap: var(--space-xs);
  }

  .mode {
    background: var(--color-bg);
    border-color: var(--color-primary);
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .field span {
    font-size: var(--font-size-xs);
    color: var(--color-fg-muted);
  }

  .field textarea {
    resize: vertical;
    min-height: 120px;
    font-family: monospace;
    font-size: var(--font-size-xs);
  }

  .checkbox {
    display: flex;
    align-items: center;
    gap: var(--space-xs);
    color: var(--color-fg);
  }

  .row {
    display: flex;
    gap: var(--space-xs);
    flex-wrap: wrap;
  }

  .count {
    margin: 0;
    color: var(--color-fg-muted);
    font-size: var(--font-size-xs);
  }

  .error {
    background: rgb(248 81 73 / 15%);
    border: 1px solid var(--color-accent-red);
    color: var(--color-accent-red);
    margin: 0;
    padding: var(--space-sm);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-sm);
  }

  button {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    color: var(--color-primary);
    padding: 4px 10px;
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-size: var(--font-size-sm);
  }

  button:hover:not(:disabled) {
    border-color: var(--color-primary);
    background: var(--color-surface-alt);
  }

  button:disabled {
    opacity: 0.5;
    cursor: default;
  }
</style>
